// Chiptune-style sound effects and background music for the roller-ski game,
// synthesised with the Web Audio API (no sound files). The context can only
// start after a user gesture, so unlock() is called from the start button.
export type SfxName =
  | "count" | "go" | "pole"
  | "bun" | "soup" | "shield" | "slow"
  | "bell" | "crash" | "over" | "horn" | "tick"
  | "rev" | "ding" | "swoosh" | "whack" | "launch" | "land";

const MUTE_KEY = "skigame-mute";
const MUSIC_KEY = "skigame-music-mute";

// Background loop: Am – F – C – G, eight eighth-notes per bar, played twice
// with a different ending the second time. MIDI note numbers, 0 = rest.
const BPM = 144;
const STEP = 60 / BPM / 2;
const ROOTS = [45, 41, 48, 43];
const LEAD = [
  [69, 72, 76, 72, 74, 72, 69, 0],
  [65, 69, 72, 69, 71, 72, 74, 0],
  [76, 74, 72, 74, 76, 79, 76, 0],
  [74, 71, 67, 71, 74, 0, 71, 0],
  [69, 72, 76, 72, 74, 72, 69, 0],
  [65, 69, 72, 69, 71, 72, 74, 0],
  [76, 74, 72, 74, 76, 79, 81, 0],
  [79, 76, 74, 71, 74, 0, 0, 0],
];
const hz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

class Sfx {
  private ctx: AudioContext | null = null;
  private out: GainNode | null = null;
  private noiseBuf: AudioBuffer | null = null;
  private musicOut: GainNode | null = null;
  private timer: number | null = null;
  private step = 0;
  private nextT = 0;
  private wantMusic = false;
  muted = false;
  musicMuted = false;

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTE_KEY) === "1";
      this.musicMuted = localStorage.getItem(MUSIC_KEY) === "1";
    } catch {
      // storage blocked: start unmuted
    }
  }

  unlock() {
    if (!this.ctx) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      this.ctx = new AC();
      this.out = this.ctx.createGain();
      this.out.gain.value = 0.45;
      this.out.connect(this.ctx.destination);
      this.musicOut = this.ctx.createGain();
      this.musicOut.gain.value = 0.35;
      this.musicOut.connect(this.out);
      const n = this.ctx.sampleRate;
      this.noiseBuf = this.ctx.createBuffer(1, n, n);
      const d = this.noiseBuf.getChannelData(0);
      for (let i = 0; i < n; i++) d[i] = Math.random() * 2 - 1;
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
  }

  setMuted(m: boolean) {
    this.muted = m;
    try {
      localStorage.setItem(MUTE_KEY, m ? "1" : "0");
    } catch {
      // not remembered, that's fine
    }
  }

  setMusicMuted(m: boolean) {
    this.musicMuted = m;
    try {
      localStorage.setItem(MUSIC_KEY, m ? "1" : "0");
    } catch {
      // not remembered, that's fine
    }
    if (m) this.halt();
    else if (this.wantMusic) this.startMusic();
  }

  // Music plays during a run and stops when it ends
  startMusic() {
    this.wantMusic = true;
    if (this.musicMuted || !this.ctx || this.timer !== null) return;
    this.step = 0;
    this.nextT = this.ctx.currentTime + 0.1;
    this.timer = window.setInterval(this.schedule, 50);
  }

  stopMusic() {
    this.wantMusic = false;
    this.halt();
  }

  private halt() {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
  }

  // Look-ahead scheduling keeps the beat steady even if a frame is late
  private schedule = () => {
    const c = this.ctx;
    if (!c) return this.halt();
    while (this.nextT < c.currentTime + 0.2) {
      this.beat(this.step, this.nextT - c.currentTime);
      this.nextT += STEP;
      this.step++;
    }
  };

  private beat(i: number, at: number) {
    const m = this.musicOut!;
    const bar = Math.floor(i / 8) % LEAD.length;
    const e = i % 8;
    this.tone(hz(ROOTS[bar % 4] + (e % 2 ? 12 : 0)), STEP * 0.9, "triangle", 0.35, at, undefined, m);
    const n = LEAD[bar][e];
    if (n) this.tone(hz(n), STEP * 0.8, "square", 0.07, at, undefined, m);
    this.noise(0.03, e % 2 ? 0.08 : 0.04, 7000, "highpass", at, undefined, m);
    if (e % 4 === 0) this.tone(150, 0.12, "sine", 0.45, at, 45, m);
  }

  // Airy puff: fades in and out, so no sharp edge
  private soft(dur: number, vol: number, from: number, to: number) {
    const c = this.ctx!;
    const t = c.currentTime;
    const s = c.createBufferSource();
    s.buffer = this.noiseBuf;
    const f = c.createBiquadFilter();
    f.type = "bandpass";
    f.Q.value = 0.6;
    f.frequency.setValueAtTime(from, t);
    f.frequency.exponentialRampToValueAtTime(to, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.linearRampToValueAtTime(vol, t + dur * 0.35);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    s.connect(f).connect(g).connect(this.out!);
    s.start(t);
    s.stop(t + dur + 0.02);
  }

  dispose() {
    this.halt();
    this.ctx?.close();
    this.ctx = null;
  }

  // A blip that fades out, optionally sliding in pitch
  private tone(f: number, dur: number, type: OscillatorType, vol: number, at = 0, to?: number, dest?: AudioNode) {
    const c = this.ctx!;
    const t = c.currentTime + at;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(f, t);
    if (to) o.frequency.exponentialRampToValueAtTime(to, t + dur);
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g).connect(dest ?? this.out!);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  // Filtered white noise, for pole ticks, skids and crashes
  private noise(dur: number, vol: number, freq: number, type: BiquadFilterType, at = 0, to?: number, dest?: AudioNode) {
    const c = this.ctx!;
    const t = c.currentTime + at;
    const s = c.createBufferSource();
    s.buffer = this.noiseBuf;
    const f = c.createBiquadFilter();
    f.type = type;
    f.frequency.setValueAtTime(freq, t);
    if (to) f.frequency.exponentialRampToValueAtTime(to, t + dur);
    const g = c.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    s.connect(f).connect(g).connect(dest ?? this.out!);
    s.start(t);
    s.stop(t + dur + 0.02);
  }

  play(name: SfxName) {
    if (this.muted || !this.ctx || this.ctx.state !== "running") return;
    switch (name) {
      case "count": this.tone(440, 0.15, "square", 0.12); break;
      case "go": this.tone(880, 0.35, "square", 0.14); break;
      case "pole": // carbide tips on asphalt: a short double tick
        this.noise(0.05, 0.2, 4200, "bandpass");
        this.tone(2400, 0.03, "triangle", 0.05);
        this.noise(0.04, 0.12, 3600, "bandpass", 0.035);
        break;
      case "bun":
        this.tone(988, 0.08, "square", 0.1);
        this.tone(1319, 0.18, "square", 0.1, 0.07);
        break;
      case "soup": // like a bun, twice over
        this.tone(988, 0.08, "square", 0.1);
        this.tone(1319, 0.1, "square", 0.1, 0.07);
        this.tone(1760, 0.18, "square", 0.1, 0.14);
        break;
      case "shield": this.tone(300, 0.45, "triangle", 0.2, 0, 1200); break;
      case "slow": this.tone(900, 0.5, "triangle", 0.2, 0, 180); break;
      case "bell": // cyclist's bell: ring-ring
        for (const at of [0, 0.13]) {
          this.tone(2100, 0.12, "sine", 0.12, at);
          this.tone(2640, 0.12, "sine", 0.07, at);
        }
        break;
      case "crash":
        this.noise(0.5, 0.45, 1200, "lowpass", 0, 120);
        this.tone(140, 0.35, "square", 0.2, 0, 40);
        break;
      case "over":
        [392, 330, 262].forEach((f, i) => this.tone(f, 0.22, "square", 0.12, 0.35 + i * 0.18));
        break;
      case "horn":
        this.tone(330, 0.32, "sawtooth", 0.06);
        this.tone(415, 0.32, "sawtooth", 0.05);
        break;
      case "tick": this.tone(1500, 0.03, "square", 0.05); break;
      case "rev": // speeder coming: an engine screaming up through the gears
        this.tone(70, 0.45, "sawtooth", 0.08, 0, 210);
        this.tone(90, 0.4, "sawtooth", 0.07, 0.42, 260);
        break;
      case "swoosh": this.soft(0.26, 0.07, 350, 1400); break;
      case "whack": // cyclist knocked off: thump, then a wail as they fly
        this.noise(0.12, 0.4, 900, "lowpass", 0, 200);
        this.tone(220, 0.2, "square", 0.15, 0, 60);
        this.tone(500, 0.45, "triangle", 0.1, 0.06, 1500);
        break;
      case "launch":
        this.noise(0.35, 0.25, 400, "bandpass", 0, 2500);
        this.tone(200, 0.35, "triangle", 0.12, 0, 600);
        break;
      case "land":
        this.noise(0.15, 0.35, 500, "lowpass", 0, 120);
        this.tone(110, 0.12, "square", 0.14, 0, 50);
        break;
      case "ding": // tram bell
        this.tone(1180, 0.5, "sine", 0.13);
        this.tone(1180, 0.5, "sine", 0.13, 0.28);
        break;
    }
  }
}

export const sfx = new Sfx();
